import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Rail,
  Arrow,
  Scroll,
  Item,
  DrawerOverlay,
  DrawerPanel,
  DrawerHeader,
  DrawerClose,
  DrawerBody,
  DrawerTitle,
  DrawerList,
  DrawerRow,
  DrawerLeft,
  DrawerDot,
  DrawerName,
  DrawerCount,
} from "./CategoryTopBarStyles";

export default function CategoryTopBar({
  categories = [],
  activeId,
  onSelect,
}) {
  const scrollerRef = useRef(null);
  const [open, setOpen] = useState(false);

  const items = useMemo(() => categories ?? [], [categories]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--cats-drawer-open",
      open ? "1" : "0",
    );

    return () => {
      document.documentElement.style.setProperty("--cats-drawer-open", "0");
    };
  }, [open]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const activeBtn = el.querySelector('[data-active="1"]');
    if (!activeBtn) return;

    activeBtn.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeId]);

  const goToCategory = (id) => {
    onSelect?.(id);

    const targetId = id ? `cat-${id}` : "cat-toate";
    const el = document.getElementById(targetId);
    if (!el) return;

    const y = el.getBoundingClientRect().top + window.scrollY;

    const offset = 68 + 56 + 10;
    window.scrollTo({ top: y - offset, behavior: "smooth" });
  };

  const pickFromDrawer = (id) => {
    goToCategory(id);
    setOpen(false);
  };

  return (
    <>
      <Rail>
        <Arrow
          type="button"
          onClick={(e) => {
            e.currentTarget.blur();
            setOpen(true);
          }}
        >
          ‹
        </Arrow>

        <Scroll ref={scrollerRef}>
          {items.map((c) => (
            <Item
              key={c.id ?? "toate"}
              $active={activeId === c.id}
              data-active={activeId === c.id ? "1" : "0"}
              onClick={(e) => {
                e.currentTarget.blur();
                goToCategory(c.id);
              }}
              type="button"
            >
              {c.name}
            </Item>
          ))}
        </Scroll>

        <Arrow
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Alege categoria (deschide)"
        >
          ›
        </Arrow>
      </Rail>

      {open && (
        <DrawerOverlay
          role="presentation"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        >
          <DrawerPanel
            role="dialog"
            aria-modal="true"
            aria-label="Categorii de produse"
            onClick={(e) => e.stopPropagation()}
          >
            <DrawerHeader>
              <DrawerTitle>Categorii de produse</DrawerTitle>
              <DrawerClose
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Închide"
              >
                ✕
              </DrawerClose>
            </DrawerHeader>

            <DrawerBody>
              <DrawerList>
                {items.map((c) => {
                  const isActive = activeId === c.id;

                  return (
                    <DrawerRow
                      key={c.id ?? "toate"}
                      type="button"
                      $active={isActive}
                      onClick={(e) => {
                        e.currentTarget.blur();
                        pickFromDrawer(c.id);
                      }}
                    >
                      <DrawerLeft>
                        <DrawerDot aria-hidden="true" $active={isActive} />
                        <DrawerName $active={isActive}>{c.name}</DrawerName>
                      </DrawerLeft>

                      {typeof c.count === "number" && (
                        <DrawerCount>({c.count})</DrawerCount>
                      )}
                    </DrawerRow>
                  );
                })}
              </DrawerList>
            </DrawerBody>
          </DrawerPanel>
        </DrawerOverlay>
      )}
    </>
  );
}
